<script setup>
/**
 * One send, in full.
 *
 * ── "NOT FOUND" IS AN EXPECTED STATE HERE, NOT A FAILURE ──────────────────────────────
 * Rows are pruned at the workspace's retention window, so a link an owner pasted into a
 * ticket three months ago points at a message that is genuinely, correctly gone. Rendering
 * that as "We couldn't load this just now — check your connection and try again" sends her
 * to look at a connection that was never broken, which is the exact defect
 * `WASLO_STATE.NOT_PROVISIONED` was invented for one screen earlier. So a 404 gets its own
 * branch: no ruby triangle, no Try again, and a sentence that names the reason.
 *
 * ── EVERYTHING AN OWNER CAN ACT ON, AND NOTHING SHE CANNOT ────────────────────────────
 * The DTO is the same projection the list row is built from, so this page can show what
 * the row had no space for rather than what a narrower read had already thrown away:
 * which rule stopped a `suppressed` send, what WhatsApp itself called the state, how many
 * tries it took, the chat it landed in, and the four instants it passed through.
 *
 * `inboxId` IS THE ONE FIELD THAT IS NOT SHOWN AS ITSELF. "Sent from 7" answers nothing,
 * so it is resolved to the channel's own name and number through the workspace's channel
 * list — a read that is allowed to fail on its own, degrading that one line rather than
 * the page. `botId`, `messageId` and `conversationReused` are internal or already said in
 * words; `terminal` is the sidecar's own verdict on whether this can still change and is
 * spent on the polling decision, not on the reader.
 *
 * ── THE METADATA IS THE PAGE'S ONE PIECE OF ARBITRARY CONTENT ─────────────────────────
 * It is whatever the owner's own system attached to the send: any shape, any length, and
 * with no obligation to contain a space. It gets `pre-wrap`, breaks at any point, and
 * lives in its own capped scroll container — README §2, where exactly one long unbroken
 * token stretched a page to 2.6× the viewport on a phone.
 */
import { computed } from 'vue';
import { useRoute } from 'vue-router';

import SettingsHeader from 'dashboard/components-next/captain/pageComponents/settings/SettingsHeader.vue';
import { frontendURL } from 'dashboard/helper/URLHelper';

import OutboundMessageReason from '../../components/outbound/OutboundMessageReason.vue';
import OutboundNotSetUp from '../../components/outbound/OutboundNotSetUp.vue';
import OutboundStatusChip from '../../components/outbound/OutboundStatusChip.vue';
import WasloPageLayout from '../../components/WasloPageLayout.vue';
import { useWasloI18n } from '../../i18n.js';
import { useWasloApi } from '../../composables/useWasloApi.js';
import { useWasloAccountId } from '../../composables/useWasloAccountId.js';
import { toIntlLocale } from '../../helpers/intl.js';
import { WASLO_ERROR_CODES, wasloApi } from '../../api/client.js';

const { t, locale } = useWasloI18n();

const accountId = useWasloAccountId();
const route = useRoute();

/** The route is the only authority, exactly as the account id is. */
const messageId = computed(() => {
  const raw = route?.params?.messageId;
  return typeof raw === 'string' && raw !== '' ? raw : null;
});

const message = useWasloApi(signal =>
  wasloApi.getOutboundMessage(accountId.value, messageId.value, signal)
);

// One extra read, for one line. `isEmpty: () => false` — a workspace with no channels at
// all is a real answer here, not this page's empty state.
const inboxes = useWasloApi(
  signal => wasloApi.getInboxes(accountId.value, signal),
  { isEmpty: () => false }
);

const row = computed(() => message.data.value);

// ── the three ways this page can have nothing to render ────────────────────────────
const code = computed(() => message.error.value?.code ?? null);

const isMissing = computed(
  () => messageId.value === null || code.value === WASLO_ERROR_CODES.NOT_FOUND
);

/**
 * The 403 for a workspace with no number wired for sending. Not a failure either, and
 * keyed on the BFF's own `outbound_not_provisioned` rather than on a bare `forbidden` —
 * that inference only held because every route here happens to be admin-only, which is
 * true today and is not a contract.
 */
const isNotSetUp = computed(
  () => code.value === WASLO_ERROR_CODES.OUTBOUND_NOT_PROVISIONED
);

const errorMessage = computed(() =>
  isMissing.value || isNotSetUp.value ? '' : message.errorMessage.value
);

const isFetching = computed(
  () => messageId.value !== null && message.isFetching.value
);

/** The "gone" state only. "Not set up" is `OutboundNotSetUp`, shared with two other pages. */
const emptyCopy = computed(() => ({
  icon: 'i-lucide-file-question',
  title: t('WASLO.OUTBOUND.LOG.DETAIL.TITLE'),
  body: t('WASLO.OUTBOUND.LOG.DETAIL.NOT_FOUND'),
}));

const backUrl = computed(() =>
  accountId.value === null
    ? ''
    : frontendURL(`accounts/${accountId.value}/waslo/outbound/log`)
);

// ── the facts ──────────────────────────────────────────────────────────────────────
const intlLocale = computed(() => toIntlLocale(locale.value));

const dateTime = iso => {
  if (typeof iso !== 'string' || iso === '') return null;
  const at = new Date(iso);
  if (Number.isNaN(at.getTime())) return null;
  return new Intl.DateTimeFormat(intlLocale.value, {
    dateStyle: 'medium',
    timeStyle: 'short',
  }).format(at);
};

/**
 * ABSOLUTE INSTANTS HERE, RELATIVE ONES IN THE LIST, and the split is deliberate. A row is
 * scanned ("this morning"); this page is the one an owner opens with her own system's log
 * open beside it, and "3 hours ago" is not something you can match a line against.
 */
const times = computed(() => {
  const value = row.value;
  if (!value) return [];
  const notYet = t('WASLO.OUTBOUND.LOG.DETAIL.TIMES.NOT_YET');
  return [
    ['CREATED', value.createdAt],
    ['ENQUEUED', value.enqueuedAt],
    ['SENT', value.sentAt],
    ['TERMINAL', value.terminalAt],
    ['UPDATED', value.updatedAt],
  ].map(([key, iso]) => ({
    key,
    label: t(`WASLO.OUTBOUND.LOG.DETAIL.TIMES.${key}`),
    value: dateTime(iso) ?? notYet,
  }));
});

const explain = computed(() => {
  const status = row.value?.status;
  if (!status) return '';
  const key = `WASLO.OUTBOUND.STATUS.EXPLAIN.${status.toUpperCase()}`;
  const sentence = t(key);
  // An unrecognised status has no explanation, and a raw key on screen is worse than a
  // missing sentence. (`t` returns the key itself when it resolves nothing.)
  return sentence === key ? '' : sentence;
});

/**
 * The channel this went out through, in the owner's own words for it.
 *
 * Three answers, all of them honest: the name (and number) we found, "a number that is no
 * longer here" when the workspace has channels and this is not one of them, and "not
 * known" while the list is still coming or after it failed to.
 */
const sender = computed(() => {
  if (!row.value) return '';
  if (inboxes.data.value === null) return t('WASLO.OUTBOUND.COMMON.UNKNOWN');
  const found = inboxes.data.value.find(
    inbox => inbox.id === row.value.inboxId
  );
  if (!found) return t('WASLO.OUTBOUND.COMMON.SENDER_UNKNOWN');
  return found.phoneNumber
    ? `${found.name} · ${found.phoneNumber}`
    : found.name;
});

/** `WHAT HAPPENED` — the state, upstream's own word for it, and how many tries it took. */
const outcomeFacts = computed(() => {
  const value = row.value;
  if (!value) return [];
  return [
    {
      key: 'PROVIDER_STATUS',
      label: t('WASLO.OUTBOUND.LOG.DETAIL.FIELDS.PROVIDER_STATUS'),
      value: value.providerStatus ?? t('WASLO.OUTBOUND.COMMON.NOT_YET'),
    },
    {
      key: 'ATTEMPTS',
      label: t('WASLO.OUTBOUND.LOG.DETAIL.FIELDS.ATTEMPTS'),
      value: t(
        'WASLO.OUTBOUND.LOG.DETAIL.FIELDS.ATTEMPTS_VALUE',
        value.attempts
      ),
    },
  ];
});

/** What was sent, to whom, through what, under whose reference. */
const sendFacts = computed(() => {
  const value = row.value;
  if (!value) return [];
  return [
    {
      key: 'TO',
      label: t('WASLO.OUTBOUND.LOG.DETAIL.FIELDS.TO'),
      value: value.to,
    },
    {
      key: 'TEMPLATE',
      label: t('WASLO.OUTBOUND.LOG.DETAIL.FIELDS.TEMPLATE'),
      value: value.template,
    },
    {
      key: 'LANGUAGE',
      label: t('WASLO.OUTBOUND.LOG.DETAIL.FIELDS.LANGUAGE'),
      value: value.language,
    },
    {
      key: 'SENDER',
      label: t('WASLO.OUTBOUND.LOG.DETAIL.FIELDS.SENDER'),
      value: sender.value,
    },
    {
      key: 'REFERENCE',
      label: t('WASLO.OUTBOUND.LOG.DETAIL.FIELDS.REFERENCE'),
      value: value.idempotencyKey,
      hint: t('WASLO.OUTBOUND.LOG.DETAIL.FIELDS.REFERENCE_HINT'),
    },
  ];
});

const isSuppressed = computed(() => row.value?.suppressedReason !== null);

/** Something went wrong only when something did — `suppressed` is not that. */
const hasFailure = computed(
  () =>
    !isSuppressed.value &&
    Boolean(row.value && (row.value.error || row.value.errorCode))
);

// ── the chat it landed in ──────────────────────────────────────────────────────────
const conversationSentence = computed(() => {
  const value = row.value;
  if (!value) return '';
  if (value.conversationId === null) {
    return t('WASLO.OUTBOUND.LOG.DETAIL.FIELDS.CONVERSATION_NONE');
  }
  return value.conversationReused
    ? t('WASLO.OUTBOUND.LOG.DETAIL.FIELDS.CONVERSATION_REUSED')
    : t('WASLO.OUTBOUND.LOG.DETAIL.FIELDS.CONVERSATION_NEW');
});

const conversationUrl = computed(() => {
  const conversationId = row.value?.conversationId ?? null;
  if (conversationId === null || accountId.value === null) return '';
  return frontendURL(
    `accounts/${accountId.value}/conversations/${conversationId}`
  );
});

// ── whatever the owner's system attached ───────────────────────────────────────────
const metadata = computed(() => {
  const value = row.value?.metadata;
  if (value === null || value === undefined) return '';
  if (
    typeof value === 'object' &&
    !Array.isArray(value) &&
    Object.keys(value).length === 0
  ) {
    return '';
  }
  try {
    return JSON.stringify(value, null, 2);
  } catch {
    // Nothing that arrived as JSON can fail to become JSON again, so this is depth
    // rather than a case — and a thrown error inside a computed blanks the section.
    return '';
  }
});
</script>

<template>
  <WasloPageLayout
    :header-title="t('WASLO.OUTBOUND.LOG.DETAIL.TITLE')"
    :back-url="backUrl"
    :back-label="t('WASLO.OUTBOUND.LOG.DETAIL.BACK')"
    :is-fetching="isFetching"
    :not-provisioned="message.isNotProvisioned.value"
    :error-message="errorMessage"
    :is-empty="isMissing || isNotSetUp"
    @retry="message.reload()"
  >
    <!-- Neither of these is a failure, so neither gets the failure's chrome. -->
    <template #emptyState>
      <OutboundNotSetUp v-if="isNotSetUp" />

      <div
        v-else
        class="flex flex-col items-center justify-center gap-3 py-16 text-center"
        data-waslo-outbound-message-missing="gone"
      >
        <span class="size-6 text-n-slate-10" :class="emptyCopy.icon" />
        <span class="text-base font-medium text-n-slate-12">
          {{ emptyCopy.title }}
        </span>
        <p class="max-w-md text-sm text-n-slate-11">{{ emptyCopy.body }}</p>
      </div>
    </template>

    <template #body>
      <div
        v-if="row"
        class="flex flex-col gap-8 pb-10"
        data-waslo-outbound-message
      >
        <!-- 1 · WHERE IT GOT TO. -->
        <section class="flex flex-col gap-4">
          <SettingsHeader
            :heading="t('WASLO.OUTBOUND.LOG.DETAIL.WHAT_HAPPENED')"
          />
          <div
            class="flex flex-col gap-4 px-5 py-5 outline outline-1 -outline-offset-1 outline-n-container rounded-xl bg-n-solid-2"
          >
            <div class="flex flex-wrap items-center gap-3">
              <span class="sr-only">
                {{ t('WASLO.OUTBOUND.LOG.DETAIL.FIELDS.STATUS') }}
              </span>
              <OutboundStatusChip :status="row.status" />
              <p v-if="explain" class="mb-0 text-sm text-n-slate-11">
                {{ explain }}
              </p>
            </div>
            <dl class="grid gap-4 mb-0 sm:grid-cols-2">
              <div
                v-for="fact in outcomeFacts"
                :key="fact.key"
                class="flex flex-col min-w-0 gap-1"
              >
                <dt class="text-sm text-n-slate-11">{{ fact.label }}</dt>
                <dd
                  class="mb-0 text-sm font-medium break-words [overflow-wrap:anywhere] text-n-slate-12"
                >
                  <bdi>{{ fact.value }}</bdi>
                </dd>
              </div>
            </dl>
          </div>
        </section>

        <!-- 2 · WHY IT DID NOT GO OUT, when it did not. Two different questions with two
             different vocabularies: a rule we applied on purpose, or a refusal upstream. -->
        <section
          v-if="isSuppressed"
          class="flex flex-col gap-4"
          data-waslo-outbound-suppressed
        >
          <SettingsHeader
            :heading="t('WASLO.OUTBOUND.LOG.DETAIL.WHY_NOT_SENT')"
          />
          <div
            class="px-5 py-5 outline outline-1 -outline-offset-1 outline-n-container rounded-xl bg-n-solid-2"
          >
            <OutboundMessageReason :message="row" />
          </div>
        </section>

        <section
          v-else-if="hasFailure"
          class="flex flex-col gap-4"
          data-waslo-outbound-failure
        >
          <SettingsHeader
            :heading="t('WASLO.OUTBOUND.LOG.DETAIL.WHY_FAILED')"
          />
          <div
            class="flex flex-col gap-4 px-5 py-5 outline outline-1 -outline-offset-1 outline-n-container rounded-xl bg-n-solid-2"
          >
            <div v-if="row.errorCode" class="flex flex-col min-w-0 gap-1">
              <span class="text-sm text-n-slate-11">
                {{ t('WASLO.OUTBOUND.LOG.DETAIL.FIELDS.ERROR_CODE') }}
              </span>
              <span
                class="text-sm font-medium break-words [overflow-wrap:anywhere] text-n-slate-12"
              >
                {{ row.errorCode }}
              </span>
            </div>
            <div v-if="row.error" class="flex flex-col min-w-0 gap-1">
              <span class="text-sm text-n-slate-11">
                {{ t('WASLO.OUTBOUND.LOG.DETAIL.FIELDS.ERROR') }}
              </span>
              <!-- Upstream's own sentence, of any length, in any direction. -->
              <p
                class="mb-0 overflow-y-auto text-sm leading-6 break-words whitespace-pre-wrap [overflow-wrap:anywhere] max-h-40 text-n-slate-12"
                dir="auto"
                data-waslo-outbound-error
              >
                {{ row.error }}
              </p>
            </div>
          </div>
        </section>

        <!-- 3 · WHAT WAS SENT, AND UNDER WHOSE REFERENCE. -->
        <section class="flex flex-col gap-4" data-waslo-outbound-send>
          <dl
            class="grid gap-4 mb-0 px-5 py-5 sm:grid-cols-2 outline outline-1 -outline-offset-1 outline-n-container rounded-xl bg-n-solid-2"
          >
            <div
              v-for="fact in sendFacts"
              :key="fact.key"
              class="flex flex-col min-w-0 gap-1"
            >
              <dt class="text-sm text-n-slate-11">{{ fact.label }}</dt>
              <!-- Every one of these is somebody else's string: a number the owner's own
                   system chose, a name WhatsApp approved, a reference her code generated.
                   Isolated and breakable, never trusted to wrap on its own (README §2). -->
              <dd
                class="mb-0 text-sm font-medium break-words [overflow-wrap:anywhere] text-n-slate-12"
              >
                <bdi>{{ fact.value }}</bdi>
              </dd>
              <p v-if="fact.hint" class="mb-0 text-xs text-n-slate-10">
                {{ fact.hint }}
              </p>
            </div>
          </dl>
        </section>

        <!-- 4 · THE CHAT IT LANDED IN. -->
        <section class="flex flex-col gap-4" data-waslo-outbound-conversation>
          <SettingsHeader
            :heading="t('WASLO.OUTBOUND.LOG.DETAIL.FIELDS.CONVERSATION')"
          />
          <div
            class="flex flex-col items-start gap-3 px-5 py-5 outline outline-1 -outline-offset-1 outline-n-container rounded-xl bg-n-solid-2"
          >
            <p class="mb-0 text-sm text-n-slate-11">
              {{ conversationSentence }}
            </p>
            <router-link
              v-if="conversationUrl"
              :to="conversationUrl"
              class="inline-flex items-center h-8 px-3 text-sm font-medium rounded-lg text-n-slate-12 bg-n-alpha-2 hover:bg-n-slate-4"
              data-waslo-outbound-open-conversation
            >
              {{ t('WASLO.OUTBOUND.LOG.DETAIL.FIELDS.OPEN_CONVERSATION') }}
            </router-link>
          </div>
        </section>

        <!-- 5 · WHATEVER HER SYSTEM ATTACHED. -->
        <section class="flex flex-col gap-4" data-waslo-outbound-metadata>
          <SettingsHeader
            :heading="t('WASLO.OUTBOUND.LOG.DETAIL.FIELDS.METADATA')"
          />
          <div
            class="px-5 py-5 outline outline-1 -outline-offset-1 outline-n-container rounded-xl bg-n-solid-2"
          >
            <p v-if="!metadata" class="mb-0 text-sm text-n-slate-11">
              {{ t('WASLO.OUTBOUND.LOG.DETAIL.FIELDS.NO_METADATA') }}
            </p>
            <!--
              NEVER A `<pre>`, and never without the cap. This is arbitrary JSON from
              somebody else's system: one unbroken token in a block that does not wrap
              takes the whole page sideways on a phone, and a hundred keys with no height
              limit buries the timestamps under it. `pre-wrap` keeps the indentation,
              `[overflow-wrap:anywhere]` breaks the token, and the box scrolls itself.
            -->
            <p
              v-else
              class="mb-0 overflow-auto font-mono text-xs leading-5 break-words whitespace-pre-wrap [overflow-wrap:anywhere] max-h-64 text-n-slate-12"
              dir="ltr"
              data-waslo-outbound-metadata-body
            >
              {{ metadata }}
            </p>
          </div>
        </section>

        <!-- 6 · WHEN EACH THING HAPPENED. -->
        <section class="flex flex-col gap-4" data-waslo-outbound-times>
          <SettingsHeader
            :heading="t('WASLO.OUTBOUND.LOG.DETAIL.TIMES.HEADING')"
          />
          <dl
            class="grid gap-4 mb-0 px-5 py-5 sm:grid-cols-2 outline outline-1 -outline-offset-1 outline-n-container rounded-xl bg-n-solid-2"
          >
            <div
              v-for="time in times"
              :key="time.key"
              class="flex flex-col min-w-0 gap-1"
            >
              <dt class="text-sm text-n-slate-11">{{ time.label }}</dt>
              <dd class="mb-0 text-sm font-medium break-words text-n-slate-12">
                {{ time.value }}
              </dd>
            </div>
          </dl>
        </section>
      </div>
    </template>
  </WasloPageLayout>
</template>
